import { memo, Fragment, useState, useEffect } from 'react';
import moment from "moment";
import type { FC } from 'react';
import type { UserData } from '@/common/types';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays, faCircleInfo, faDiceD20, faLeaf, faPenToSquare, faPersonChalkboard, faShare, faTrash, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { auxInfoEnum } from "@/common/consts";
import Swal from 'sweetalert2';

type Props = {
    pollStyle: string;
    userData: UserData[];
    activeUserId: number;
    isHost: boolean;
    dateSlot: string[];
    auxInfoCodes: string[];
    extraQuestionIds: number[];
    timeslotHostLock: boolean;
    timeslotShift: number;

    login: ((userName: string, userId: number) => void);
    switchCellColour: ((date: string, timeslotIdx: number) => void);
    deleteUser: ((userName: string, userId: number) => void);
    setAuxInfoModal: ((n : any) => void);
};

export const ScheduleTable: FC<Props> = memo(({ 
    pollStyle,
    userData,
    activeUserId,
    isHost,
    auxInfoCodes,
    extraQuestionIds,
    timeslotHostLock,
    timeslotShift,
    dateSlot,

    login,
    switchCellColour,
    deleteUser,
    setAuxInfoModal,
}) => {
    const [ hostClosed, setHostClosed ] = useState<string[]>([]);
    useEffect(() => {
        let newHostClosed : string[] = [];
        for (const user of userData) {
            if (user.host) {
                for (const dateKey in user.attendance) {
                    if (!user.attendance[dateKey]) {
                        newHostClosed.push(dateKey);
                    }
                }
            }
        }

        setHostClosed([...new Set(newHostClosed)]);
    }, [userData]);

    const [ dates, setDates ] = useState<string[]>([]);
    useEffect(() => {
        setDates(dateSlot);
    }, [dateSlot])

    //-------------------------- AUX COMPONENTS --------------------------
    const TimeslotCell = (date: string, timeslotIdx: number, user: UserData) => {
        const dateKey = date + "-" + timeslotIdx.toString();
        let attType = "open";

        if (hostClosed.includes(dateKey) && timeslotHostLock) {
            attType = "closed"; 
        } else if (dateKey in user.attendance) {
            if (user.attendance[dateKey]) {
                attType = "preferred";
            } else {
                attType = "closed";
            }
        }

        if (timeslotShift > 0) {
            if ((date == dates[dates.length - 1]) && (timeslotIdx >= timeslotShift)) {
                attType += " disabled"
            } else if ((date == dates[0]) && (timeslotIdx < timeslotShift)) {
                attType += " disabled"
            }
        } else if (timeslotShift < 0) {
            if ((date == dates[0]) && (timeslotIdx < 48 + timeslotShift)) {
                attType += " disabled"
            } else if ((date == dates[dates.length - 1]) && (timeslotIdx >= 48 + timeslotShift)) {
                attType += " disabled"
            }
        } 
        
        if (!attType.includes("disabled")) {
            if (hostClosed.includes(dateKey)) {
                attType += " host-unavail";

                if ((timeslotHostLock) && (!user.host || !isHost)) {
                    attType += " cursor-not-allowed";
                }
            }

            if (timeslotIdx % 2 == 0) {
                attType += " even";
            } else {
                attType += " odd";
            }
        }

        if (timeslotIdx == 0) {
            attType += " timeslotidx-0";
        } else if (timeslotIdx == 47) {
            attType += " timeslotidx-47";
        }

        if (!attType.includes("cursor-not-allowed")) {
            attType += " cursor-pointer";
        }

        if ((activeUserId == user.id) && (!attType.includes("disabled")) && ((!hostClosed.includes(dateKey)) || (isHost) || (!timeslotHostLock))) {
            return <td 
                key={user.id + "-" + date + "-" + timeslotIdx} className={attType} 
                onMouseDown={() => switchCellColour(date, timeslotIdx)} onMouseEnter={(ev) => {
                    if (ev.buttons === 1) {
                        switchCellColour(date, timeslotIdx);
                    }
                }}
            >
                {
                    (attType.includes("host-unavail") && (!user.host)) ? 
                        <div className='flex flex-row opacity-75'>
                            <FontAwesomeIcon icon={faTriangleExclamation} className='mx-auto' />
                        </div>                    
                    :
                    null
                }                
            </td>;
        } else {
            return <td 
                key={user.id + "-" + date + "-" + timeslotIdx}
                className={attType}
            >
                {
                    (attType.includes("host-unavail") && (!user.host)) ? 
                        <div className='flex flex-row opacity-75'>
                            <FontAwesomeIcon icon={faTriangleExclamation} className='mx-auto' />
                        </div>                    
                    :
                    null
                }
            </td>;
        }
    }

    const DateHeader = (date: string, dateIdx: number) => {
        let className = "date-header " + pollStyle.toLowerCase();
        if (pollStyle == "VERTICAL") {
            if (dateIdx == 0) {
                className += " first";
            }
        }

        return(
            <th key={'header-' + date} id={"DateHeader-" + date} className={className} colSpan={(pollStyle == "VERTICAL") ? 49 : 48}>
                <div className='flex flex-row'>
                    <div className='flex flex-row items-center justify-center mx-auto'>
                        <div>{moment(date).format('YYYY-MM-DD (dddd)')}</div>
                        <p title='Jump to date' className='cursor-pointer' 
                            onClick={async () => {
                                const res = await Swal.fire({
                                    title: "Jump to date",
                                    input: "date",
                                    theme: "dark",
                                    confirmButtonText: "Jump",
                                    inputAttributes: {
                                        min: dates[0] ?? "",
                                        max: dates[dates.length - 1] ?? "",
                                    },
                                    inputValue: date
                                })

                                if (res.isConfirmed) {
                                    const dateEl = document.getElementById("DateHeader-" + res.value);
                                    if (dateEl) {
                                        if (pollStyle == "VERTICAL") {
                                            dateEl.scrollIntoView({
                                                behavior: "smooth",
                                                block: "start",
                                            })
                                        } else {
                                            dateEl.scrollIntoView({
                                                behavior: "smooth",
                                                inline: "end"
                                            })
                                        }
                                    }
                                }
                            }}
                        >
                            <FontAwesomeIcon icon={faCalendarDays} className='mt-1'/>
                        </p>
                    </div>
                </div>
            </th>
        )
    }

    return (
        <div className='relative grow select-none flex flex-col'>
            <div className="max-h-full w-full flex flex-col absolute overflow-auto ms-2 pe-2 pb-2">
                {
                    (pollStyle == "HORIZONTAL") ?
                    <table className='poll me-2 horizontal' style={{ width: 'auto' }}>
                        <thead className="sticky top-0" style={{ zIndex: 2 }}>
                            <tr>
                                <th rowSpan={2} className='sticky left-0 top-0 align-middle text-center text-2xl first-col' style={{ zIndex: 6 }}>Name</th>
                                { dates.map((date, dateIdx) => DateHeader(date, dateIdx))}
                            </tr>
                            <tr>
                                { dates.map((date, dateIdx) => {
                                    return Array.from(Array(24*2).keys()).map((timeslotIdx) => (
                                        <th key={date + "-" + timeslotIdx} style={{ height: '3ch' }}
                                            className={"timeslot horizontal relative min-w-[3.5ch] max-w-[3.5ch] text-start" 
                                                + ((timeslotIdx % 2 == 0) ? " even" : " odd") 
                                                + ((timeslotIdx == 47) ? " timeslotidx-47" : "")}
                                        >
                                            <div className="absolute ps-2 top-0" style={{ zIndex: 4 }}>{(timeslotIdx % 2 == 0) ? timeslotIdx/2 : ""}</div>                                                        
                                        </th>
                                    ))
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {userData.map((user, idx) => (
                                <tr key={'tr-' + idx} style={{ height: '5ch' }} className={(user.id == activeUserId) ? "selected-row" : ""}>
                                    <th className='sticky text-nowrap left-0 align-middle px-3 min-w-[30ch] name-cell first-col' style={{ zIndex: 1 }}>
                                        <div className='flex flex-row items-center gap-3 w-full'>
                                            <div className="me-auto">{user.name}</div>
                                            {
                                                (user.host == true) ?
                                                <div className='flex flex-row items-center gap-1'>
                                                    <div>GM</div>
                                                    <FontAwesomeIcon icon={faDiceD20}/>
                                                </div>
                                                : null
                                            }
                                            { auxInfoCodes.includes(auxInfoEnum.firstTimer) && (user.auxInfo[auxInfoEnum.firstTimer] ?? false) ?
                                                    <FontAwesomeIcon icon={faLeaf} className="text-green-600" />
                                                : null
                                            }
                                            {
                                                (activeUserId == -1) ?
                                                    user.passExist ?
                                                        <Fragment>
                                                            <FontAwesomeIcon className="cursor-pointer" icon={faPenToSquare} onClick={() => login(user.name, user.id)}/>
                                                        </Fragment>
                                                    : null
                                                : ((isHost) && (user.id !== activeUserId)) ?
                                                <Fragment>
                                                    { auxInfoCodes.includes(auxInfoEnum.helpCharCreate) && (user.auxInfo[auxInfoEnum.helpCharCreate] ?? false) ?
                                                            <FontAwesomeIcon icon={faPersonChalkboard}/>
                                                        : null
                                                    }
                                                    {
                                                        ((auxInfoCodes.length > 0) || (extraQuestionIds.length > 0)) ?
                                                        <FontAwesomeIcon className="cursor-pointer" icon={faCircleInfo} onClick={() => setAuxInfoModal({
                                                            show: true,
                                                            name: user.name,
                                                            auxInfo: {
                                                                [auxInfoEnum.discordHandle]: user.auxInfo[auxInfoEnum.discordHandle] ?? "",
                                                                [auxInfoEnum.veils]: user.auxInfo[auxInfoEnum.veils] ?? "",
                                                                [auxInfoEnum.lines]: user.auxInfo[auxInfoEnum.lines] ?? "",
                                                                ...Object.fromEntries(extraQuestionIds.map(questionId => ["q-" + questionId, user.auxInfo["q-" + questionId] ?? ""]))
                                                            }
                                                        })}/>
                                                        : null
                                                    }
                                                    <FontAwesomeIcon className="cursor-pointer text-red-500" icon={faTrash} onClick={() => deleteUser(user.name, user.id)}/>
                                                </Fragment>
                                                : null
                                            }
                                        </div>
                                    </th>
                                    {dates.map((date) => {
                                        return Array.from(Array(24*2).keys()).map((idx2) => TimeslotCell(date, idx2, user))
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    : (pollStyle == "VERTICAL") ?
                    <table className='poll w-full vertical' style={{ width: 'auto' }}>
                        <tbody>
                            {dates.map((date, dateIdx) => 
                                <Fragment key={'v-' + date}>
                                    <tr>{DateHeader(date, dateIdx)}</tr>
                                    <tr>
                                        <th className="sticky left-0 left-border" style={{ zIndex:6 }}></th>
                                        { Array.from(Array(24*2).keys()).map((timeslotIdx) => (
                                            <th key={date + "-" + timeslotIdx} style={{ height: "4ch"}}
                                                className={"timeslot horizontal relative text-start" 
                                                    + ((timeslotIdx % 2 == 0) ? " even" : " odd") 
                                                    + ((timeslotIdx == 0) ? " timeslotidx-0" : (timeslotIdx == 47) ? " timeslotidx-47" : "")
                                                    + " min-w-[3.5ch] max-w-[3.5ch]"
                                                }
                                            >
                                                <div className="absolute ps-2 pb-1 bottom-0" style={{ zIndex: 4 }}>{(timeslotIdx % 2 == 0) ? timeslotIdx/2 : ""}</div>                                                        
                                            </th>
                                        ))}
                                    </tr>
                                    {userData.map((user, idx) => (
                                        <tr key={'tr-' + idx} className={(user.id == activeUserId) ? "selected-row" : ""} style={{ height: '3em' }}>
                                            <th className={'sticky text-nowrap left-0 align-middle px-3 w-[310px] left-border name-cell'
                                                + ((idx == userData.length - 1) ? " bottom-border" : "")
                                            } style={{ zIndex: 1 }}>
                                                <div className='flex flex-row items-center gap-3 w-full'>
                                                    <div className="me-auto">{user.name}</div>
                                                    {
                                                        (user.host == true) ?
                                                        <div className='flex flex-row items-center gap-1'>
                                                            <div>GM</div>
                                                            <FontAwesomeIcon icon={faDiceD20}/>
                                                        </div>
                                                        : null
                                                    }
                                                    { auxInfoCodes.includes(auxInfoEnum.firstTimer) && (user.auxInfo[auxInfoEnum.firstTimer] ?? false) ?
                                                            <FontAwesomeIcon icon={faLeaf} className="text-green-600" />
                                                        : null
                                                    }
                                                    {
                                                        (activeUserId == -1) ?
                                                            user.passExist ?
                                                                <Fragment>
                                                                    <FontAwesomeIcon className="cursor-pointer" icon={faPenToSquare} onClick={() => login(user.name, user.id)}/>
                                                                </Fragment>
                                                            : null
                                                        : ((isHost) && (user.id !== activeUserId)) ?
                                                        <Fragment>
                                                            { auxInfoCodes.includes(auxInfoEnum.helpCharCreate) && (user.auxInfo[auxInfoEnum.helpCharCreate] ?? false) ?
                                                                    <FontAwesomeIcon icon={faPersonChalkboard}/>
                                                                : null
                                                            }
                                                            {
                                                                ((auxInfoCodes.length > 0) || (extraQuestionIds.length > 0)) ?
                                                                <FontAwesomeIcon className="cursor-pointer" icon={faCircleInfo} onClick={() => setAuxInfoModal({
                                                                    show: true,
                                                                    name: user.name,
                                                                    auxInfo: {
                                                                        [auxInfoEnum.discordHandle]: user.auxInfo[auxInfoEnum.discordHandle] ?? "",
                                                                        [auxInfoEnum.veils]: user.auxInfo[auxInfoEnum.veils] ?? "",
                                                                        [auxInfoEnum.lines]: user.auxInfo[auxInfoEnum.lines] ?? "",
                                                                        ...Object.fromEntries(extraQuestionIds.map(questionId => ["q-" + questionId, user.auxInfo["q-" + questionId] ?? ""]))
                                                                    }
                                                                })}/>
                                                                : null
                                                            }
                                                            <FontAwesomeIcon className="cursor-pointer text-red-500" icon={faTrash} onClick={() => deleteUser(user.name, user.id)}/>
                                                        </Fragment>
                                                        : null
                                                    }
                                                </div>
                                            </th>
                                            { Array.from(Array(24*2).keys()).map((idx2) => TimeslotCell(date, idx2, user)) }
                                        </tr>
                                    ))}
                                </Fragment>
                            )}
                        </tbody>
                    </table>
                    : null
                }
            </div>
        </div>
    )
});